/**
 * SynergyProcessor.ts - Handles synergy calculations based on scenario configuration
 * 
 * This processor replaces hardcoded synergy logic with dynamic scenario-based synergies
 */

import { Investment } from '../types/Investment';
import { Incident, ResponseOption } from '../types/Incident';
import { SynergyMapping, SynergyEffects, ResponseConditions } from '../types/ScenarioManagement';

export interface SynergyResult {
  mitigationBonus: number;
  securityBonus: number;
  reputationBonus: number;
  financialBonus: number;
  operationalBonus: number;
  activeSynergies: ActiveSynergy[];
}

export interface ActiveSynergy {
  synergyId: string;
  name: string;
  description: string;
  effectsApplied: Partial<SynergyEffects>;
}

export class SynergyProcessor {
  private synergies: SynergyMapping[];
  private logger: Console;

  constructor(synergies: SynergyMapping[] = [], enableLogging: boolean = true) {
    this.synergies = synergies.filter(s => s.enabled !== false);
    // Disable logging in test environment
    const isTestEnv = process.env.NODE_ENV === 'test';
    this.logger = (enableLogging && !isTestEnv) ? console : { log: () => {}, info: () => {}, warn: () => {}, error: () => {} } as Console;
  }

  /**
   * Calculate synergies for a specific response option
   */
  calculateResponseSynergies(
    option: ResponseOption,
    incident: Incident,
    investments: Investment[]
  ): SynergyResult {
    const result: SynergyResult = {
      mitigationBonus: 0,
      securityBonus: 0,
      reputationBonus: 0,
      financialBonus: 0,
      operationalBonus: 0,
      activeSynergies: []
    };

    // Get active investment IDs
    const activeInvestmentIds = new Set(investments.map(inv => inv.id));

    // Sort synergies by priority (higher priority first)
    const sortedSynergies = [...this.synergies].sort((a, b) => 
      (b.priority || 0) - (a.priority || 0)
    );

    // Process each synergy
    for (const synergy of sortedSynergies) {
      if (this.isSynergyActive(synergy, activeInvestmentIds, incident, option)) {
        const effects = this.applySynergyEffects(synergy, option, result);
        
        result.activeSynergies.push({
          synergyId: synergy.id,
          name: synergy.name,
          description: synergy.description,
          effectsApplied: effects
        });

        this.logger.info(`Synergy activated: ${synergy.name}`, {
          synergyId: synergy.id,
          investments: synergy.requiredInvestmentIds,
          effects
        });
      }
    }

    // Apply caps to bonuses
    result.mitigationBonus = Math.min(result.mitigationBonus, 0.3); // Cap at 30% additional mitigation
    result.securityBonus = Math.min(result.securityBonus, 0.5);     // Cap at 50% bonus
    result.reputationBonus = Math.min(result.reputationBonus, 0.5);
    result.financialBonus = Math.min(result.financialBonus, 0.5);
    result.operationalBonus = Math.min(result.operationalBonus, 0.5);

    return result;
  }

  /**
   * Check if a synergy is active based on investments and conditions
   */
  private isSynergyActive(
    synergy: SynergyMapping,
    activeInvestmentIds: Set<string>,
    incident: Incident,
    option: ResponseOption
  ): boolean {
    // Check if all required investments are active
    const hasAllRequiredInvestments = synergy.requiredInvestmentIds.every(
      id => activeInvestmentIds.has(id)
    );

    if (!hasAllRequiredInvestments) {
      return false;
    }

    // Check additional conditions if present
    if (synergy.conditions) {
      return this.checkConditions(synergy.conditions, incident, option, activeInvestmentIds);
    }

    return true;
  }

  /**
   * Check if conditions are met for synergy activation
   */
  private checkConditions(
    conditions: ResponseConditions,
    incident: Incident,
    option: ResponseOption,
    activeInvestmentIds: Set<string>
  ): boolean {
    // Check incident category condition
    if (conditions.incidentCategories && conditions.incidentCategories.length > 0) {
      if (!conditions.incidentCategories.includes(incident.category)) {
        return false;
      }
    }

    // Check incident severity condition
    if (conditions.incidentSeverities && conditions.incidentSeverities.length > 0) {
      if (!conditions.incidentSeverities.includes(incident.severity)) {
        return false;
      }
    }

    // Check specific incident IDs
    if (conditions.specificIncidentIds && conditions.specificIncidentIds.length > 0) {
      if (!conditions.specificIncidentIds.includes(incident.id)) {
        return false;
      }
    }

    // Check specific response IDs
    if (conditions.specificResponseIds && conditions.specificResponseIds.length > 0) {
      if (!conditions.specificResponseIds.includes(option.id)) {
        return false;
      }
    }

    // Check minimum investment count
    if (conditions.minInvestmentCount !== undefined) {
      if (activeInvestmentIds.size < conditions.minInvestmentCount) {
        return false;
      }
    }

    // Custom condition evaluation
    if (conditions.customCondition) {
      // Simple implementation for response label/description matching
      if (conditions.customCondition.includes('response.label contains')) {
        const keywords = conditions.customCondition
          .replace('response.label contains ', '')
          .split('|')
          .map(k => k.trim());
        
        const labelLower = (option.label || '').toLowerCase();
        const descLower = (option.description || '').toLowerCase();
        
        const matches = keywords.some(keyword => 
          labelLower.includes(keyword.toLowerCase()) ||
          descLower.includes(keyword.toLowerCase())
        );
        
        if (!matches) {
          return false;
        }
      } else {
        this.logger.warn('Complex custom conditions not yet fully implemented', {
          condition: conditions.customCondition
        });
      }
    }

    return true;
  }

  /**
   * Apply synergy effects to the result
   */
  private applySynergyEffects(
    synergy: SynergyMapping,
    option: ResponseOption,
    currentResult: SynergyResult
  ): Partial<SynergyEffects> {
    const appliedEffects: Partial<SynergyEffects> = {};
    const effects = synergy.effects;

    // Apply response multipliers
    if (effects.responseMultipliers) {
      const multipliers = effects.responseMultipliers;
      const defaultMultiplier = multipliers.default || 0;

      // Apply category-specific multipliers or default
      currentResult.securityBonus += (multipliers.security ?? defaultMultiplier);
      currentResult.reputationBonus += (multipliers.reputation ?? defaultMultiplier);
      currentResult.financialBonus += (multipliers.financial ?? defaultMultiplier);
      currentResult.operationalBonus += (multipliers.operational ?? defaultMultiplier);

      appliedEffects.responseMultipliers = multipliers;
    }

    // Apply response bonuses (fixed bonuses)
    if (effects.responseBonuses) {
      const bonuses = effects.responseBonuses;
      
      if (bonuses.security !== undefined) {
        currentResult.securityBonus += bonuses.security / 100; // Convert to decimal
      }
      if (bonuses.reputation !== undefined) {
        currentResult.reputationBonus += bonuses.reputation / 100;
      }
      if (bonuses.financial !== undefined) {
        currentResult.financialBonus += bonuses.financial / 100;
      }
      if (bonuses.operational !== undefined) {
        currentResult.operationalBonus += bonuses.operational / 100;
      }

      appliedEffects.responseBonuses = bonuses;
    }

    // Apply impact mitigation
    if (effects.impactMitigation !== undefined) {
      currentResult.mitigationBonus += effects.impactMitigation / 100; // Convert percentage to decimal
      appliedEffects.impactMitigation = effects.impactMitigation;
    }

    // Note: timeReduction and customScoring are stored but not applied here
    // They should be handled by the calculator or other systems
    if (effects.timeReduction) {
      appliedEffects.timeReduction = effects.timeReduction;
    }
    if (effects.customScoring) {
      appliedEffects.customScoring = effects.customScoring;
    }

    return appliedEffects;
  }

  /**
   * Get all active synergies for a set of investments (without specific response)
   */
  getActiveSynergies(investments: Investment[]): ActiveSynergy[] {
    const activeInvestmentIds = new Set(investments.map(inv => inv.id));
    const activeSynergies: ActiveSynergy[] = [];

    for (const synergy of this.synergies) {
      const hasAllRequiredInvestments = synergy.requiredInvestmentIds.every(
        id => activeInvestmentIds.has(id)
      );

      if (hasAllRequiredInvestments) {
        activeSynergies.push({
          synergyId: synergy.id,
          name: synergy.name,
          description: synergy.description,
          effectsApplied: synergy.effects
        });
      }
    }

    return activeSynergies;
  }

  /**
   * Update synergies configuration
   */
  updateSynergies(synergies: SynergyMapping[]): void {
    this.synergies = synergies.filter(s => s.enabled !== false);
    this.logger.info('Synergies updated', {
      count: this.synergies.length,
      synergies: this.synergies.map(s => ({ id: s.id, name: s.name }))
    });
  }

  /**
   * Get synergy by ID
   */
  getSynergy(synergyId: string): SynergyMapping | undefined {
    return this.synergies.find(s => s.id === synergyId);
  }

  /**
   * Check if any synergies are configured
   */
  hasSynergies(): boolean {
    return this.synergies.length > 0;
  }
}